import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Mail, Lock, User, CheckCircle2, MailCheck, Sparkles, Eye, EyeOff } from "lucide-react";
import logoLiderIA from "@/assets/logolideria.png";
import { SEO } from "@/components/SEO";
import { FloatingWhatsAppButton } from "@/components/FloatingWhatsAppButton";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const safeNext = (raw: string | null): string | null => {
  if (!raw) return null;
  // Only allow same-origin relative paths.
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
};

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const nextParam = safeNext(searchParams.get("next") ?? searchParams.get("redirect"));
  const postAuthTarget = nextParam ?? "/dashboard";
  const defaultTab = searchParams.get("mode") === "signup" ? "signup" : "login";
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirm, setShowSignupConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [magicLinkCooldown, setMagicLinkCooldown] = useState(0);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  // Tick cooldown timer
  useEffect(() => {
    if (magicLinkCooldown <= 0) return;
    const t = setTimeout(() => setMagicLinkCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [magicLinkCooldown]);

  const describeOAuthError = (raw: string): string => {
    const msg = (raw || "").toLowerCase();
    if (msg.includes("missing oauth secret") || msg.includes("unsupported provider")) {
      return "El proveedor de Google aún no está configurado en el servidor. Contacta al administrador para completar la configuración de OAuth.";
    }
    if (msg.includes("popup") || msg.includes("blocked")) {
      return "El navegador bloqueó la ventana de Google. Habilita los popups e intenta de nuevo.";
    }
    return raw || "No se pudo iniciar sesión con Google";
  };

  const handleGoogleSignIn = async () => {
      setIsGoogleLoading(true);
      try {
        // Use Lovable Cloud Auth on any deployed origin (Lovable's own subdomains
        // and custom domains alike). Only fall back to direct Supabase OAuth in
        // local development, where redirecting through oauth.lovable.app wouldn't
        // be able to come back to localhost.
        const host = window.location.hostname;
        const isLocalDev = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');

        // Preserve `next` (e.g. the OAuth consent URL) through the Google round-trip.
        const returnTo = window.location.origin + postAuthTarget;
        if (!isLocalDev) {
          const result = await lovable.auth.signInWithOAuth("google", {
            redirect_uri: returnTo,
          });
          if (result.error) {
            toast({ title: "Error con Google", description: describeOAuthError(String(result.error)), variant: "destructive" });
            return;
          }
          if (result.redirected) return;
          navigate(postAuthTarget);
        } else {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: returnTo
            }
          });
          if (error) {
            toast({ title: "Error con Google", description: describeOAuthError(error.message), variant: "destructive" });
          }
        }
      } catch (err: any) {
        toast({ title: "Error", description: describeOAuthError(err?.message ?? ""), variant: "destructive" });
      } finally {
        setIsGoogleLoading(false);
      }
    };
  useEffect(() => {
    if (user) navigate(postAuthTarget, { replace: true });
  }, [user, navigate, postAuthTarget]);

  // Surface OAuth callback errors returned from Supabase via query/hash params
  // (Supabase redirects back here with ?error_code=... when provider config is broken).
  useEffect(() => {
    const hash = window.location.hash?.startsWith("#") ? window.location.hash.slice(1) : "";
    const hashParams = new URLSearchParams(hash);
    const errCode = searchParams.get("error_code") || hashParams.get("error_code");
    const errMsg = searchParams.get("error_description") || hashParams.get("error_description") || searchParams.get("msg");
    if (errCode || errMsg) {
      toast({
        title: "No se pudo iniciar sesión",
        description: describeOAuthError(errMsg || errCode || ""),
        variant: "destructive",
      });
    }
  }, [searchParams, toast]);

  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "signup") setActiveTab("signup");
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword,
    });

    if (error) {
      const authErrors: Record<string, string> = {
        "Invalid login credentials": "Correo o contraseña incorrectos.",
        "Email not confirmed": "Debes verificar tu correo electrónico antes de iniciar sesión.",
        "Invalid Refresh Token: Refresh Token Not Found": "Tu sesión ha expirado. Por favor, inicia sesión de nuevo.",
        "User not found": "No se encontró una cuenta con ese correo.",
        "Too many requests": "Demasiados intentos. Espera unos minutos e intenta de nuevo.",
        "Email rate limit exceeded": "Se ha excedido el límite de envíos. Intenta más tarde.",
        "Password should be at least 6 characters": "La contraseña debe tener al menos 6 caracteres.",
        "User already registered": "Ya existe una cuenta con este correo electrónico.",
        "Signup requires a valid password": "Debes ingresar una contraseña válida.",
      };
      const translatedMsg = authErrors[error.message] || error.message;
      toast({
        title: "Error al iniciar sesión",
        description: translatedMsg,
        variant: "destructive",
      });
    } else {
      toast({ title: "¡Bienvenido!", description: "Has iniciado sesión correctamente." });
      navigate(postAuthTarget);
    }
    setIsLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (signupPassword !== signupConfirmPassword) {
      toast({ title: "Error", description: "Las contraseñas no coinciden.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    if (signupPassword.length < 6) {
      toast({ title: "Error", description: "La contraseña debe tener al menos 6 caracteres.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    const { error } = await supabase.auth.signUp({
      email: signupEmail,
      password: signupPassword,
      options: {
        data: { full_name: signupName },
        emailRedirectTo: window.location.origin + postAuthTarget,
      },
    });

    if (error) {
      toast({ title: "Error al registrarse", description: error.message, variant: "destructive" });
    } else {
      // Referral attribution is handled centrally by useReferralAttribution
      // once the session exists (works for email signup and Google alike).
      setShowEmailConfirmation(true);
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    // Always show the same success message for security (don't reveal if email exists)
    if (error) {
      console.error('[reset-password] Error:', error.message);
    }

    toast({
      title: "Correo enviado",
      description: "Si la dirección está registrada, recibirás un correo para restablecer tu contraseña.",
    });
    setForgotPassword(false);
    setIsLoading(false);
  };

  const handleMagicLink = async () => {
    if (!loginEmail) {
      toast({ title: "Correo requerido", description: "Ingresa tu correo para enviarte el enlace de acceso.", variant: "destructive" });
      return;
    }
    if (magicLinkCooldown > 0) {
      toast({
        title: "Espera unos segundos",
        description: `Por seguridad, puedes solicitar otro enlace en ${magicLinkCooldown}s.`,
      });
      return;
    }
    setIsMagicLinkLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: loginEmail.trim().toLowerCase(),
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        shouldCreateUser: false,
      },
    });
    setIsMagicLinkLoading(false);

    if (error) {
      console.error('[magic-link] Error:', error);
      const msg = (error.message || '').toLowerCase();
      const status = (error as any).status;

      // Rate limit (Supabase devuelve 429 o mensaje "rate limit"/"security purposes")
      if (status === 429 || msg.includes('rate limit') || msg.includes('security purposes') || msg.includes('only request this after')) {
        // Extraer segundos si vienen en el mensaje ("after 47 seconds")
        const match = msg.match(/(\d+)\s*second/);
        const secs = match ? parseInt(match[1], 10) : 60;
        setMagicLinkCooldown(secs);
        toast({
          title: "Demasiados intentos",
          description: `Espera ${secs} segundos antes de solicitar otro enlace. Revisa tu bandeja (y spam) por si el anterior ya llegó.`,
          variant: "destructive",
        });
        return;
      }

      // Usuario no existe / signups deshabilitados → mantenemos mensaje genérico por seguridad
      if (msg.includes('signups not allowed') || msg.includes('user not found')) {
        setMagicLinkCooldown(60);
        toast({
          title: "Enlace enviado",
          description: "Si la dirección está registrada, recibirás un correo con un enlace para iniciar sesión. Revisa también la carpeta de spam.",
        });
        return;
      }

      // Otros errores → mostrar al usuario para diagnóstico
      toast({
        title: "No se pudo enviar el enlace",
        description: error.message || "Intenta nuevamente en unos minutos.",
        variant: "destructive",
      });
      return;
    }

    // Éxito → cooldown de 60s para evitar que el usuario dispare el rate limit
    setMagicLinkCooldown(60);
    toast({
      title: "Enlace enviado",
      description: "Revisa tu bandeja de entrada (y la carpeta de spam). El correo puede tardar hasta 1 minuto en llegar.",
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Iniciar sesión o crear cuenta — LÍDER IA"
        description="Accede a tu cuenta de LÍDER IA o crea una nueva para comenzar tu ruta de aprendizaje en Inteligencia Artificial."
        path="/auth"
        noindex
      />
      <header className="p-4 border-b border-border">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => window.history.length > 1 ? navigate(-1) : navigate("/")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <img src={logoLiderIA} alt="Líder IA" className="h-8" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {showEmailConfirmation ? (
            <Card className="border-border/50 shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <MailCheck className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">¡Revisa tu correo!</CardTitle>
                <CardDescription className="text-base mt-2">
                  Hemos enviado un enlace de confirmación a{" "}
                  <span className="font-semibold text-foreground">{signupEmail}</span>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Antes de iniciar sesión:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Abre tu bandeja de entrada</li>
                      <li>Busca el correo de Líder IA</li>
                      <li>Haz clic en el enlace de confirmación</li>
                    </ol>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  ¿No lo encuentras? Revisa tu carpeta de spam o correo no deseado.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowEmailConfirmation(false);
                    setActiveTab("login");
                  }}
                >
                  Ir a Iniciar Sesión
                </Button>
              </CardContent>
            </Card>
          ) : (
          <Card className="border-border/50 shadow-lg">
            <CardHeader className="text-center">
              <img src={logoLiderIA} alt="Líder IA" className="h-10 mx-auto mb-4" />
              <CardTitle className="text-2xl">Bienvenido a Líder IA</CardTitle>
              <CardDescription>
                {forgotPassword ? "Ingresa tu correo para restablecer tu contraseña" : "Accede a tu cuenta o crea una nueva para comenzar"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {forgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Correo electrónico</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input id="reset-email" type="email" placeholder="tu@email.com" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} className="pl-10" required />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Enviando..." : "Enviar enlace de recuperación"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => setForgotPassword(false)}>
                    Volver al inicio de sesión
                  </Button>
                </form>
              ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="login">Iniciar Sesión</TabsTrigger>
                    <TabsTrigger value="signup">Registrarse</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Correo electrónico</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input id="login-email" type="email" placeholder="tu@email.com" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className="pl-10" required />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="login-password">Contraseña</Label>
                          <button type="button" onClick={() => setForgotPassword(true)} className="text-sm text-primary hover:underline">
                            ¿Olvidaste tu contraseña?
                          </button>
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input id="login-password" type={showLoginPassword ? "text" : "password"} placeholder="••••••••" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="pl-10 pr-10" required />
                          <button type="button" onClick={() => setShowLoginPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? "Ingresando..." : "Iniciar Sesión"}
                      </Button>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                        <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">o continúa con</span></div>
                      </div>
                      <Button type="button" variant="outline" className="w-full" disabled={isGoogleLoading} onClick={handleGoogleSignIn}>
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        {isGoogleLoading ? "Conectando..." : "Google"}
                      </Button>
                      <Button type="button" variant="outline" className="w-full" disabled={isMagicLinkLoading || magicLinkCooldown > 0} onClick={handleMagicLink}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        {isMagicLinkLoading
                          ? "Enviando..."
                          : magicLinkCooldown > 0
                            ? `Reintenta en ${magicLinkCooldown}s`
                            : "Enviarme un enlace de acceso"}
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="signup">
                    <form onSubmit={handleSignup} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="signup-name">Nombre completo</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input id="signup-name" type="text" placeholder="Tu nombre" value={signupName} onChange={(e) => setSignupName(e.target.value)} className="pl-10" required />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-email">Correo electrónico</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input id="signup-email" type="email" placeholder="tu@email.com" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} className="pl-10" required />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-password">Contraseña</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input id="signup-password" type={showSignupPassword ? "text" : "password"} placeholder="••••••••" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} className="pl-10 pr-10" required />
                          <button type="button" onClick={() => setShowSignupPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-confirm-password">Confirmar contraseña</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input id="signup-confirm-password" type={showSignupConfirm ? "text" : "password"} placeholder="••••••••" value={signupConfirmPassword} onChange={(e) => setSignupConfirmPassword(e.target.value)} className="pl-10 pr-10" required />
                          <button type="button" onClick={() => setShowSignupConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            {showSignupConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? "Creando cuenta..." : "Crear Cuenta"}
                      </Button>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                        <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">o continúa con</span></div>
                      </div>
                      <Button type="button" variant="outline" className="w-full" disabled={isGoogleLoading} onClick={handleGoogleSignIn}>
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        {isGoogleLoading ? "Conectando..." : "Google"}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              )}

              <div className="mt-6 text-center text-sm text-muted-foreground">
                Al continuar, aceptas nuestros{" "}
                <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Términos de Servicio</a>{" "}y{" "}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Política de Privacidad</a>
              </div>
            </CardContent>
          </Card>
          )}
        </div>
      </main>
      <FloatingWhatsAppButton />
    </div>
  );
};

export default AuthPage;
